/**
 * 11.1 — a experiência do Parceiro (lote P3).
 *
 * ## O que estava servido e não estava consumido
 *
 * Quatro endpoints existiam desde o materializador e nenhuma tela os lia: trajetória,
 * composição, extrato e pipeline. E o resumo devolvia seis campos, dos quais a tela
 * usava três. Não é API nova — é consumo do que já estava materializado, e é a
 * diferença entre "três KPIs e uma tabela de quatro colunas" e a tela que a spec
 * descreve.
 *
 * ## A lei do período aqui é a exceção 2 da spec 05 §3
 *
 * Pendência não prescreve: toda linha NÃO PAGA aparece sempre, em qualquer período. O
 * período governa só o histórico de **Pagas**, pela data de pagamento. É por isso que o
 * filtro vai para a consulta e ainda assim "A receber" não encolhe quando alguém escolhe
 * "Hoje" — quem espera receber não deixa de esperar porque mudou o recorte da tela.
 *
 * ## "Apurada" não é "liberada"
 *
 * A trajetória mostra comissão APURADA: a soma das linhas reais do mês. Não é um
 * percentual sobre o bruto — a liberação é a quitação de cada parcela do cliente (D-174, DS-618), e as
 * duas contas divergem sempre que o cliente paga em parcelas.
 *
 * ## "Todas" num portal de várias empresas (D-131)
 *
 * Cada comissão é de UMA empresa beneficiária, e em Todas a tabela ganha a coluna Empresa. O
 * negócio do pipeline chega uma vez (a API deduplica), com a participação já SOMADA das
 * empresas do portal (DS-527): a tela mostra o número que recebe, sem somar de novo.
 */
import { useEffect, useMemo, useState } from 'react'
import { FileText } from 'lucide-react'
import { z } from 'zod'
import {
  comissaoDoParceiroNoPortal,
  composicaoDoParceiroNoPortal,
  documentoDoPortal,
  linhaDoExtratoDoParceiroNoPortal,
  negocioDoPipelineNoPortal,
  resumoDoParceiroNoPortal,
  trajetoriaDoParceiroNoPortal,
  type ComissaoDoParceiroNoPortal,
  type LinhaDoExtratoDoParceiroNoPortal,
  type ResumoDoParceiroNoPortal,
} from '@dv/contracts'
import { resolverPeriodo, type DataNominal, type Periodo } from '@dv/domain'
import { pedir } from '../api/cliente.js'
import { juntar, useConsulta } from '../estado/recurso.js'
import { Erro, Esqueleto, Vazio } from '../componentes/estados.js'
import { rotuloCompleto, SeletorDePeriodo } from '../componentes/periodo.js'
import { SeletorComCapa } from '../componentes/capa.js'
import { useAviso } from '../componentes/toast.js'
import { useEmpresas } from '../estado/empresas.js'
import { br, brCurto, dataBr, maiorDe, mesAno, mesCurto, mesesAte, percentualBr, proporcao, somar } from '../formato.js'
import { CampanhasDoParceiro } from './campanhas-do-parceiro.js'
import { Documentos } from './documentos.js'
import { ExtratoParaFaturamento } from './extrato.js'

const listaDeComissoes = z.array(comissaoDoParceiroNoPortal)
const listaDeTrajetoria = z.array(trajetoriaDoParceiroNoPortal)
const listaDoExtrato = z.array(linhaDoExtratoDoParceiroNoPortal)
const listaDoPipeline = z.array(negocioDoPipelineNoPortal)
const listaDeDocumentos = z.array(documentoDoPortal)
/** O endpoint devolve UMA linha de agregação; a resposta é a lista dela. */
const resumoEmLista = z.array(resumoDoParceiroNoPortal)

const COR_DO_STATUS: Readonly<Record<ComissaoDoParceiroNoPortal['statusLinha'], string>> = {
  Liberada: 'navy',
  'Aguardando cliente': 'ambar',
  'A faturar': 'slate',
  Prevista: 'slate',
  Paga: 'verde',
}

const STATUS = ['Liberada', 'Aguardando cliente', 'A faturar', 'Prevista', 'Paga'] as const

/**
 * Estágio de pipeline usa ESCALA DE INTENSIDADE em navy, não um arco-íris (D-54).
 *
 * Quanto mais perto de fechar, mais forte o navy. "Novo" fica em slate porque ainda não
 * há nada a fazer com ele, e o que não pede ação não pede cor. Estágio desconhecido cai
 * em slate: um nome que este código não conhece não vira intensidade inventada.
 */
const COR_DO_ESTAGIO: Readonly<Record<string, string>> = {
  Novo: 'slate',
  Qualificação: 'navy-fraco',
  Proposta: 'navy-fraco',
  Negociação: 'navy-medio',
  Fechamento: 'navy-forte',
}

interface BlocoQueDestrava {
  readonly chave: ComissaoDoParceiroNoPortal['statusBloco']
  readonly ponto: string
  readonly titulo: string
  readonly texto: string
}

/**
 * Os três blocos da faixa, na ordem em que o dinheiro anda.
 *
 * O ponto colorido é o único lugar onde a cor entra: o cartão é branco com o número em
 * navy (D-54). Cada bloco diz o VALOR, porque a pergunta do parceiro é "quanto", e a
 * contagem sozinha nunca respondeu isso.
 */
const BLOCOS: readonly BlocoQueDestrava[] = [
  {
    chave: 'pronto_para_faturar',
    ponto: 'ponto-navy',
    titulo: 'Pronto para faturar',
    texto: 'O cliente já pagou. Emita a sua nota contra quem o extrato indica.',
  },
  {
    chave: 'esperando_cliente',
    ponto: 'ponto-ambar',
    titulo: 'Esperando o cliente pagar',
    texto: 'A nota já saiu para o cliente. Sua comissão libera quando o pagamento entrar.',
  },
  {
    chave: 'nao_faturado',
    ponto: 'ponto-slate',
    titulo: 'Ainda não faturado ao cliente',
    texto: 'A nota da DigitalView ainda não saiu. Sua comissão entra na régua depois disso.',
  },
]

/** De onde vem a contagem de cada cartão da faixa: o resumo, que conta pedidos distintos. */
const QUANTIDADE_DO_BLOCO: Readonly<Record<BlocoQueDestrava['chave'], (resumo: ResumoDoParceiroNoPortal) => string>> = {
  pronto_para_faturar: (resumo) => resumo.pedidosProntos,
  esperando_cliente: (resumo) => resumo.pedidosEsperando,
  nao_faturado: (resumo) => resumo.pedidosNaoFaturados,
}

export function ExperienciaDoParceiro({ hoje, empresa, aoMudarPeriodo }: {
  hoje: DataNominal
  empresa: string
  /** O rótulo do período sobe para a linha do "Bem-vindo", na casca (PK-06A). */
  aoMudarPeriodo: (rotulo: string) => void
}) {
  const [periodo, setPeriodo] = useState<Periodo>(() => resolverPeriodo('este_mes', hoje))
  const rotuloDoPeriodo = rotuloCompleto(periodo)
  useEffect(() => {
    aoMudarPeriodo(rotuloDoPeriodo)
  }, [aoMudarPeriodo, rotuloDoPeriodo])
  const [status, setStatus] = useState('')
  const [extratoAberto, setExtratoAberto] = useState(false)
  const avisar = useAviso()
  const empresas = useEmpresas()

  const chaveDoPeriodo = `${periodo.de}|${periodo.ate}|${status}`
  const parametros = (): string => {
    const p = new URLSearchParams({ de: periodo.de, ate: periodo.ate })
    if (status !== '') p.set('status', status)
    return p.toString()
  }

  const resumo = useConsulta('parceiro/resumo', () => pedir('parceiro/resumo', resumoEmLista))
  const comissoes = useConsulta(`comissoes|${chaveDoPeriodo}`, () =>
    pedir(`parceiro/comissoes?${parametros()}`, listaDeComissoes),
  )
  /*
    A faixa "o que destrava" NÃO responde ao filtro de status, e por isso ela tem a
    própria consulta ao mesmo endpoint.

    Ela é o retrato dos TRÊS blocos ao mesmo tempo. Somando a lista da tabela, um filtro
    em "Liberada" deixaria os outros dois cartões em R$ 0,00 — um fato financeiro falso,
    e justamente o que o filtro não afirmou. O período fica na consulta só para não
    arrastar o histórico inteiro de pagas; ele não muda o resultado, porque a faixa soma
    apenas o que NÃO está pago, e linha não paga volta em qualquer recorte.
  */
  const blocos = useConsulta(`blocos|${periodo.de}|${periodo.ate}`, () =>
    pedir(`parceiro/comissoes?de=${periodo.de}&ate=${periodo.ate}`, listaDeComissoes),
  )
  const trajetoria = useConsulta('parceiro/trajetoria', () =>
    pedir('parceiro/trajetoria', listaDeTrajetoria),
  )
  const composicao = useConsulta('parceiro/composicao', () =>
    pedir('parceiro/composicao', composicaoDoParceiroNoPortal),
  )
  const pipeline = useConsulta('parceiro/pipeline', () => pedir('parceiro/pipeline', listaDoPipeline))
  const extrato = useConsulta('parceiro/extrato', () => pedir('parceiro/extrato', listaDoExtrato))
  const documentos = useConsulta('parceiro/documentos', () =>
    pedir('parceiro/documentos', listaDeDocumentos),
  )

  const conjunto = juntar([
    resumo.recurso,
    comissoes.recurso,
    blocos.recurso,
    trajetoria.recurso,
    composicao.recurso,
    pipeline.recurso,
    extrato.recurso,
    documentos.recurso,
  ])

  if (conjunto !== 'carregando' && conjunto !== 'pronto') {
    return (
      <Erro
        tipo={conjunto}
        tentar={() => {
          resumo.recarregar()
          comissoes.recarregar()
          blocos.recarregar()
          trajetoria.recarregar()
          composicao.recarregar()
          pipeline.recarregar()
          extrato.recarregar()
          documentos.recarregar()
        }}
      />
    )
  }

  const totais = resumo.recurso.estado === 'pronto' ? resumo.recurso.dados[0] : undefined
  const linhas = comissoes.recurso.estado === 'pronto' ? comissoes.recurso.dados : []
  const meses = trajetoria.recurso.estado === 'pronto' ? trajetoria.recurso.dados : []
  const fatias = composicao.recurso.estado === 'pronto' ? composicao.recurso.dados : { clientes: [], produtos: [] }
  const negocios = pipeline.recurso.estado === 'pronto' ? pipeline.recurso.dados : []
  const liberadas = extrato.recurso.estado === 'pronto' ? extrato.recurso.dados : []
  const arquivos = documentos.recurso.estado === 'pronto' ? documentos.recurso.dados : []

  const pipelineAtivo = somar(negocios.map((n) => n.valor))
  /* A faixa soma o que ainda NÃO foi pago, e nada mais: pago não destrava nada. */
  const pendentes = (blocos.recurso.estado === 'pronto' ? blocos.recurso.dados : []).filter(
    (linha) => linha.statusLinha !== 'Paga',
  )
  const proximo = proximoVencimento(pendentes)
  const percentual = recorteDoPercentual(blocos.recurso.estado === 'pronto' ? blocos.recurso.dados : null)

  return (
    <>
      {/* O subtítulo só carrega dado: o recorte e, quando há, a condição de comissão (D-203). */}
      <p className="subtitulo-da-pagina" data-teste="subtitulo">
        {empresa}
        {percentual === null ? null : ` · ${percentual}`}
      </p>

      {conjunto === 'carregando' || totais === undefined ? (
        <Esqueleto linhas={4} teste="esqueleto-kpis" />
      ) : (
        <div className="kpis quatro" data-teste="kpis">
          {/* "(total)" o separa do "Total a receber" da tabela, que muda com o filtro (spec 11.1:46). */}
          <Kpi rotulo="A receber (total)" valor={br(totais.aReceber)} />
          <Kpi rotulo="Liberadas" valor={br(totais.liberadas)} />
          <Kpi rotulo="Aguardando cliente" valor={br(totais.aguardandoCliente)} />
          {/* O quarto cartão: Σ dos negócios abertos em que o parceiro participa. */}
          <Kpi rotulo="Pipeline ativo" valor={br(pipelineAtivo)} />
        </div>
      )}

      {/*
        A faixa mora SOBRE O PAPEL, entre os KPIs e a seção de comissões, como os cartões de indicador
        do app interno (fluxo-de-caixa.tsx:326, :449): dentro do painel ela ficava acima do título que
        dá nome ao painel, cartão dentro de cartão (D-134 · DS-531).
      */}
      {blocos.recurso.estado === 'pronto' && extrato.recurso.estado === 'pronto' && totais !== undefined ? (
        <section className="destrava-cartoes" data-teste="o-que-destrava" aria-label="O que destrava cada bloco">
          {BLOCOS.map((bloco) => {
            const doBloco = pendentes.filter((linha) => linha.statusBloco === bloco.chave)
            // Pronto para faturar é o que as parcelas quitadas liberaram no extrato, não a comissão bruta.
            const pronto = bloco.chave === 'pronto_para_faturar'
            /*
              A contagem é de PEDIDOS, e quem conta é o servidor (spec 11.1: agregação por
              `status_bloco`, pedidos DISTINTOS — DS-527). Contar linhas na tela dizia "2 pedidos"
              para o pedido comissionado a duas empresas do portal, e o código do pedido só é
              único dentro de uma entidade DV. O conjunto é o mesmo da faixa: o resumo conta as
              linhas NÃO PAGAS de cada bloco, e a faixa soma as mesmas, que voltam em qualquer
              período (o período só recorta Pagas).
            */
            const quantidade = QUANTIDADE_DO_BLOCO[bloco.chave](totais)
            const valor = pronto ? somar(liberadas.map((linha) => linha.liberadaValor)) : somar(doBloco.map((linha) => linha.valorComissao))
            return (
              <article key={bloco.chave} className="destrava-cartao">
                {/* O rótulo reserva duas linhas e a contagem é irmã dele, no topo: é o que alinha os três valores. */}
                <div className="destrava-topo">
                  <p className="destrava-titulo">
                    <span className={`ponto ${bloco.ponto}`} aria-hidden="true" />
                    {bloco.titulo}
                  </p>
                  <span className="destrava-contagem" data-teste="contagem-do-bloco">
                    {quantidade === '1' ? '1 pedido' : `${quantidade} pedidos`}
                  </span>
                </div>
                <strong>{br(valor)}</strong>
                <p className="destrava-texto">
                  {bloco.chave === 'esperando_cliente' && proximo !== null
                    ? `${bloco.texto} ${proximo}`
                    : bloco.texto}
                </p>
                {bloco.chave === 'pronto_para_faturar' && (
                  <button
                    type="button"
                    className="link"
                    onClick={() => {
                      setExtratoAberto(true)
                    }}
                    data-teste="ver-extrato"
                  >
                    Ver extrato →
                  </button>
                )}
              </article>
            )
          })}
        </section>
      ) : (
        <Esqueleto linhas={3} teste="esqueleto-destrava" />
      )}

      <section className="painel" data-teste="comissoes">
        <div className="titulo">
          {/* Os títulos do protótipo 11.1, sem eyebrow acima (PK-09). */}
          <h2>Comissões a receber</h2>
          <div className="filtros">
            <SeletorDePeriodo hoje={hoje} periodo={periodo} aoTrocar={setPeriodo} />
            <label className="filtro">
              <span className="sr-only">Status</span>
              <SeletorComCapa
                value={status}
                onChange={(evento) => {
                  setStatus(evento.target.value)
                }}
                data-teste="filtro-status"
              >
                <option value="">Todos os status</option>
                {STATUS.map((item) => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </SeletorComCapa>
            </label>
            {/*
              O extrato ganha porta no cabeçalho da seção.

              O único gatilho vivia dentro da faixa, que só existe depois de a consulta
              voltar: quem entrava com a rede lenta não tinha caminho nenhum para o
              documento que ele precisa emitir. Aqui o botão existe sempre, e o modal se
              vira sozinho quando não há nada liberado.
            */}
            <button
              type="button"
              className="primario acao-do-titulo"
              onClick={() => {
                setExtratoAberto(true)
              }}
              data-teste="extrato-para-faturamento"
            >
              <FileText size={16} /> Extrato para faturamento
            </button>
          </div>
        </div>

        {comissoes.recurso.estado === 'carregando' ? (
          <Esqueleto linhas={5} teste="esqueleto-comissoes" />
        ) : linhas.length === 0 ? (
          <Vazio
            titulo={status === '' ? 'Nenhuma comissão registrada ainda' : 'Nada neste status'}
            texto={
              status === ''
                ? 'Quando uma venda sua gerar comissão, ela aparece aqui.'
                : 'Ajuste o filtro para ver outras parcelas.'
            }
          />
        ) : (
          <TabelaDeComissoes
            linhas={linhas}
            status={status}
            aoDetalhar={(linha) => {
              avisar({
                tom: 'info',
                titulo: linha.pedidoCodigo,
                texto: linha.nota ?? '',
                teste: 'detalhe-da-linha',
              })
            }}
          />
        )}
      </section>

      <Trajetoria meses={meses} hoje={hoje} />
      <Composicao clientes={fatias.clientes} produtos={fatias.produtos} />
      {negocios.length > 0 && (
        <section className="painel" data-teste="pipeline">
          <div className="titulo">
            <h2>Pipeline em que você participa</h2>
          </div>
          {/* Um negócio por linha, no cartão leve das campanhas; a seção não muda de lugar (D-134 · DS-531). */}
          <div className="lista-cartoes">
            {negocios.map((negocio) => (
              <article key={negocio.negocioCodigo} className="cartao-item" data-teste="cartao-negocio">
                <div className="cartao-topo">
                  <p className="eyebrow">{negocio.negocioCodigo}</p>
                  <span className={`badge ${COR_DO_ESTAGIO[negocio.estagio] ?? 'slate'}`}>{negocio.estagio}</span>
                </div>
                <h3 title={negocio.nome}>{negocio.nome}</h3>
                {empresas.mostrar && (
                  <p className="empresas-do-cartao" data-teste="empresas-do-negocio">
                    {empresas.nomesDe(negocio.empresaIds)}
                  </p>
                )}
                {/* Sem participação atribuída o negócio aparece assim mesmo: sumir com
                    ele esconderia do parceiro um negócio em que ele está. */}
                <p className="cartao-rodape" data-teste="rodape-do-negocio">
                  <span className="valor tnum">{br(negocio.valor)}</span>
                  {' · sua participação: '}
                  {negocio.participacao === null ? '—' : percentualBr(negocio.participacao)}
                </p>
              </article>
            ))}
          </div>
        </section>
      )}

      {/*
        P-48: as campanhas dos pedidos em que o parceiro é parte, com os resultados que a DigitalView publicou para ele,
        sem o valor. Consulta própria, fora do `juntar`: a falha dela fica no bloco e não derruba as comissões (DS-677).
        O retrato da página vai na chave dela: a troca do período ou do status é uma consulta nova do bloco também.
      */}
      <CampanhasDoParceiro mostrar={conjunto === 'pronto'} retrato={chaveDoPeriodo} />

      <Documentos itens={arquivos} comAbas={false} titulo="Documentos compartilhados" />

      {extratoAberto && (
        <ExtratoParaFaturamento
          linhas={liberadas}
          aoFechar={() => {
            setExtratoAberto(false)
          }}
        />
      )}
    </>
  )
}

function TabelaDeComissoes({
  linhas,
  status,
  aoDetalhar,
}: {
  linhas: readonly ComissaoDoParceiroNoPortal[]
  status: string
  aoDetalhar: (linha: ComissaoDoParceiroNoPortal) => void
}) {
  const empresas = useEmpresas()
  /*
    O rodapé muda de significado com o filtro, e por isso muda de rótulo.

    Sem filtro, "Total a receber" é a soma das NÃO PAGAS — pagar já aconteceu, e somar
    o que já entrou daria um total que ninguém espera receber. Com filtro, o total é do
    que está na tela, e o sufixo diz isso com todas as letras.
  */
  const semFiltro = status === ''
  const total = semFiltro
    ? somar(linhas.filter((l) => l.statusLinha !== 'Paga').map((l) => l.valorComissao))
    : somar(linhas.map((l) => l.valorComissao))

  return (
    <div className="tabela dv-xscroll">
      <table className="dv-tabela" data-teste="tabela-comissoes-do-parceiro">
        <thead>
          <tr>
            <th>Pedido</th>
            {/* Em Todas, a empresa beneficiária. Sem classe: a elástica já é o Cliente. */}
            {empresas.mostrar && <th>Empresa</th>}
            <th className="dv-elastica">Cliente</th>
            <th>Base × %</th>
            <th>Valor</th>
            <th>Previsão / pagto.</th>
            <th>Status</th>
            <th>Detalhes</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((linha) => {
            // D-74: o anunciante só entra quando informado E diferente do cliente.
            const nome =
              linha.anuncianteNome !== null && linha.anuncianteNome !== linha.clienteNome
                ? `${linha.clienteNome} (${linha.anuncianteNome})`
                : linha.clienteNome
            return (
              <tr key={linha.id} data-teste="linha-comissao">
                <td>
                  <span className="codigo tnum">{linha.pedidoCodigo}</span>
                  {linha.refExterna !== null && <span className="linha-apoio">Ref. {linha.refExterna}</span>}
                </td>
                {empresas.mostrar && <td data-teste="empresa-da-linha">{empresas.nomeDe(linha.empresaId)}</td>}
                <td className="dv-elastica">
                  {/*
                    O nome corta numa linha, com teto e o texto inteiro no title, como a coluna
                    Cliente do app interno (campanhas.tsx:565; P18 · DS-531): a razão social longa
                    com o anunciante quebrava em duas linhas e empurrava o código para o meio.
                  */}
                  <span className="nome-da-linha" title={nome}>
                    {nome}
                  </span>
                  {/*
                    A sublinha é a SITUAÇÃO da linha, e não o anunciante.

                    É ela que responde "por que este valor ainda não é meu" sem exigir um
                    clique — que é a pergunta que traz o parceiro a esta tela. O anunciante
                    continua ali, entre parênteses ao lado do cliente, onde ele qualifica o
                    nome em vez de ocupar a linha inteira. O texto completo fica no título
                    do elemento e no aviso de "Detalhes".
                  */}
                  {linha.nota !== null && (
                    <span className="nota-da-linha" title={linha.nota}>
                      {linha.nota}
                    </span>
                  )}
                </td>
                <td className="tnum texto-secundario">
                  {br(linha.baseValor)}
                  {linha.percentual === null ? '' : ` × ${percentualBr(linha.percentual)}`}
                </td>
                <td className="tnum valor">{br(linha.valorComissao)}</td>
                <td className="tnum">{previsaoOuPagamento(linha)}</td>
                <td>
                  <span className={`badge ${COR_DO_STATUS[linha.statusLinha]}`}>{linha.statusLinha}</span>
                </td>
                <td>
                  {linha.nota === null ? (
                    '—'
                  ) : (
                    <button
                      type="button"
                      className="link"
                      data-teste="detalhes-da-comissao"
                      onClick={() => {
                        aoDetalhar(linha)
                      }}
                    >
                      Detalhes
                    </button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={empresas.mostrar ? 4 : 3} className="rotulo-do-total">
              {semFiltro ? 'Total a receber' : 'Total exibido (filtro aplicado)'}
            </td>
            <td className="tnum total" data-teste="total-comissoes">{br(total)}</td>
            <td colSpan={3} />
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

function previsaoOuPagamento(linha: ComissaoDoParceiroNoPortal): string {
  if (linha.pagamentoData !== null) return `pago em ${dataBr(linha.pagamentoData)}`
  // Só a LIBERADA tem data firme; antes disso o vencimento depende do cliente pagar (0134:121 preenche o campo em qualquer status).
  if (linha.statusLinha === 'Liberada' && linha.liberadaVencimento !== null) return dataBr(linha.liberadaVencimento)
  const prevista = linha.liberadaVencimento ?? linha.previsaoPagamento
  return prevista === null ? '—' : `prev. ${dataBr(prevista)}`
}

function proximoVencimento(linhas: readonly ComissaoDoParceiroNoPortal[]): string | null {
  const datas = linhas
    .filter((l) => l.statusLinha === 'Aguardando cliente' && l.previsaoPagamento !== null)
    .map((l) => l.previsaoPagamento)
    .filter((d): d is DataNominal => d !== null)
    .sort()
  const primeira = datas[0]
  return primeira === undefined ? null : `Próximo vencimento em ${dataBr(primeira)}.`
}

/**
 * O que o subtítulo pode AFIRMAR sobre o percentual.
 *
 * O acordo é por pedido, não por empresa: duas linhas podem ter percentuais diferentes,
 * e escrever um deles no alto da tela transformaria a exceção em regra. Só quando todas
 * concordam existe "o seu percentual"; quando não, o subtítulo manda ler a coluna, que é
 * onde o número de cada pedido está.
 */
function recorteDoPercentual(linhas: readonly ComissaoDoParceiroNoPortal[] | null): string | null {
  // Ainda carregando, ou nenhuma comissão registrada: nada a afirmar, e o subtítulo diz só o
  // recorte. A frase que descrevia a tela repetia o título (D-203).
  if (linhas === null || linhas.length === 0) return null
  if (linhas.some(linha => linha.percentual === null)) return 'condições de comissão por pedido, na tabela'
  const distintos = new Set(
    linhas.map((linha) => linha.percentual).filter((valor): valor is string => valor !== null),
  )
  const unico = [...distintos][0]
  if (distintos.size !== 1 || unico === undefined) return 'percentual por pedido, na coluna Base × %'
  return `comissão ${percentualBr(unico)} sobre as vendas que você originou`
}

function Trajetoria({
  meses,
  hoje,
}: {
  meses: readonly { competencia: string; vendas: string; comissao: string; pedidos: string }[]
  hoje: DataNominal
}) {
  const resumo = useMemo(() => {
    const vendas = somar(meses.map((m) => m.vendas))
    const comissao = somar(meses.map((m) => m.comissao))
    const pedidos = meses.reduce((total, m) => total + Number(m.pedidos), 0)
    const melhor = [...meses].sort((a, b) => Number(b.comissao) - Number(a.comissao))[0]
    return { vendas, comissao, pedidos, melhor }
  }, [meses])

  if (meses.length === 0) return null

  return (
    <section className="painel" data-teste="trajetoria">
      {/* Sem apoio: "vendas originadas e comissão apurada, mês a mês" repetia os rótulos logo abaixo e o ⓘ (D-203). */}
      <div className="titulo">
        <h2>Sua trajetória</h2>
      </div>
      {/*
        O resumo é INDICADOR, não frase: eyebrow, valor de 20px em navy e o apoio de 11px, como os
        indicadores da DRE (dre.tsx:187-193), sem a caixa, porque ele já mora num cartão (DS-531).
      */}
      <dl className="resumo-do-ano" data-teste="resumo-do-ano">
        <div>
          <dt className="eyebrow">Vendas originadas</dt>
          <dd>{br(resumo.vendas)}</dd>
        </div>
        <div>
          <dt className="eyebrow">Comissão apurada</dt>
          <dd>{br(resumo.comissao)}</dd>
        </div>
        <div>
          <dt className="eyebrow">Venda média</dt>
          <dd>{resumo.pedidos === 0 ? '—' : br(dividir(resumo.vendas, resumo.pedidos))}</dd>
        </div>
        {resumo.melhor !== undefined && (
          <div>
            <dt className="eyebrow">Melhor mês</dt>
            <dd>{mesAno(resumo.melhor.competencia)}</dd>
            <dd className="resumo-apoio">comissão {br(resumo.melhor.comissao)}</dd>
          </div>
        )}
      </dl>
      <GraficoDaTrajetoria meses={meses} hoje={hoje} />
      <div className="tabela dv-xscroll">
        <table className="dv-tabela" data-teste="tabela-trajetoria">
          <thead>
            <tr>
              <th>Mês</th>
              <th>Vendas originadas</th>
              <th>Comissão apurada</th>
              {/* Sem coluna de nome, a elástica é a ÚLTIMA: a sobra fica no fim e o mês junto dos números (DS-531). */}
              <th className="dv-elastica">Acumulado no ano</th>
            </tr>
          </thead>
          <tbody>
            {acumular(meses).map((mes) => (
              <tr key={mes.competencia} data-teste="linha-trajetoria">
                <td>{mesAno(mes.competencia)}</td>
                <td className="tnum">{br(mes.vendas)}</td>
                <td className="tnum valor">{br(mes.comissao)}</td>
                <td className="dv-elastica tnum texto-secundario">{br(mes.acumulado)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

interface MesDoGrafico {
  readonly chave: string
  readonly vendas: string
  readonly comissao: string
  readonly comDado: boolean
}

/**
 * A trajetória em desenho: barras são vendas originadas, a linha é comissão apurada.
 *
 * ## Por que duas escalas, e por que isso é DITO
 *
 * As duas séries têm ordens de grandeza diferentes por definição: a comissão é uma
 * fração da venda. Desenhadas na mesma escala, a comissão vira um risco rente ao chão e
 * o gráfico não mostra nada. Cada série ganha a própria escala, e a legenda diz isso com
 * todas as letras: duas escalas caladas fariam o leitor comparar altura com altura e
 * concluir o que não está escrito.
 *
 * ## 12 meses fixos; mês ausente é zero
 *
 * O eixo é a janela fixa da `CurvaDeSaldo` do app interno (tesouraria.tsx:941-963): os 12 meses
 * que terminam no mês de hoje. Um passo por mês DO DADO fazia um mês só virar um bloco do tamanho
 * do cartão. A API só omite o mês sem venda comissionada (portal-leitura.controller.ts:55-59),
 * então o mês ausente é ZERO nas duas séries, e a linha sempre tem 12 pontos.
 *
 * ## Cada mês é um <button> com o valor em texto (CLAUDE.md, Todo gráfico é INTERATIVO)
 *
 * O balão no cursor e no foco diz o mês, as vendas e a comissão, e vira de lado depois do meio;
 * a barra sob o cursor se destaca, a altura muda com transição, e o teclado alcança cada mês.
 * O traço é SVG que não deforma (`vector-effect`), e o marcador é HTML, para não virar elipse.
 *
 * A geometria sai de `proporcao`, que compara em centavos inteiros: nenhum valor de
 * dinheiro passa por ponto flutuante para virar altura de barra.
 */
function GraficoDaTrajetoria({ meses, hoje }: {
  meses: readonly { competencia: string; vendas: string; comissao: string }[]
  hoje: DataNominal
}) {
  const [aceso, setAceso] = useState<number | null>(null)
  const janela = useMemo((): MesDoGrafico[] => mesesAte(hoje, 12).map((chave) => {
    const doMes = meses.find((mes) => mes.competencia.slice(0, 7) === chave)
    return doMes === undefined
      ? { chave, vendas: '0.00', comissao: '0.00', comDado: false }
      : { chave, vendas: doMes.vendas, comissao: doMes.comissao, comDado: true }
  }), [meses, hoje])
  const tetoDeVendas = maiorDe(janela.map((mes) => mes.vendas))
  const tetoDeComissao = maiorDe(janela.map((mes) => mes.comissao))
  /** Percentual da altura útil; 90% é o respiro do topo. Zero é zero, sem piso. */
  const alturaDe = (valor: string, teto: string): number => {
    const parte = proporcao(valor, teto)
    return parte === 0 ? 0 : Math.max(2, parte * 90)
  }
  /** Centro da coluna do mês, em % da largura (tesouraria.tsx:1001-1011). */
  const centro = (indice: number): number => ((indice + 0.5) / janela.length) * 100
  const linha = janela
    .map((mes, indice) => `${indice === 0 ? 'M' : 'L'}${String(centro(indice) * 12)} ${String(100 - alturaDe(mes.comissao, tetoDeComissao))}`)
    .join(' ')
  const selecionado = aceso === null ? undefined : janela[aceso]
  return (
    <figure className="grafico" data-teste="grafico-da-trajetoria">
      <figcaption>
        <span className="chave-barra" aria-hidden="true" /> Vendas originadas
        <span className="chave-linha" aria-hidden="true" /> Comissão apurada
        <small>escalas independentes</small>
      </figcaption>
      <div className="grafico-area" onMouseLeave={() => { setAceso(null) }}>
        <div className="grafico-colunas">
          {janela.map((mes, indice) => (
            <button
              key={mes.chave}
              type="button"
              data-teste="mes-da-trajetoria"
              aria-label={`${mesAno(mes.chave)}: vendas ${br(mes.vendas)}, comissão ${br(mes.comissao)}`}
              onMouseEnter={() => { setAceso(indice) }}
              onFocus={() => { setAceso(indice) }}
              onBlur={() => { setAceso(null) }}
            >
              <span
                className={aceso === indice ? 'barra-do-mes acesa' : 'barra-do-mes'}
                style={{ height: `${String(alturaDe(mes.vendas, tetoDeVendas))}%` }}
              />
            </button>
          ))}
        </div>
        <svg viewBox="0 0 1200 100" preserveAspectRatio="none" aria-hidden="true">
          {aceso === null ? null : <line className="guia-do-mes" x1={centro(aceso) * 12} x2={centro(aceso) * 12} y1={0} y2={100} />}
          <path className="linha-comissao" d={linha} data-teste="linha-da-comissao" />
        </svg>
        {janela.map((mes, indice) => (mes.comDado || aceso === indice ? (
          <span
            key={mes.chave}
            className={aceso === indice ? 'marcador-comissao aceso' : 'marcador-comissao'}
            aria-hidden="true"
            style={{ left: `${String(centro(indice))}%`, top: `${String(100 - alturaDe(mes.comissao, tetoDeComissao))}%` }}
          />
        ) : null))}
        {aceso === null || selecionado === undefined ? null : (
          <div
            className="balao-da-trajetoria"
            data-teste="balao-da-trajetoria"
            style={aceso >= janela.length / 2
              ? { right: `${String(100 - centro(aceso))}%`, marginRight: 8 }
              : { left: `${String(centro(aceso))}%`, marginLeft: 8 }}
          >
            <p className="eyebrow">{mesAno(selecionado.chave)}</p>
            <p>Vendas {br(selecionado.vendas)}</p>
            <p className="balao-comissao">Comissão {br(selecionado.comissao)}</p>
          </div>
        )}
      </div>
      <div className="grafico-meses" aria-hidden="true">
        {janela.map((mes) => <span key={mes.chave}>{mesCurto(mes.chave)}</span>)}
      </div>
    </figure>
  )
}

/** Acumulado no ano, mês a mês — a coluna que a spec pede. */
function acumular(
  meses: readonly { competencia: string; vendas: string; comissao: string; pedidos: string }[],
): { competencia: string; vendas: string; comissao: string; acumulado: string }[] {
  let corrente: string[] = []
  return meses.map((mes) => {
    corrente = [...corrente, mes.comissao]
    return { competencia: mes.competencia, vendas: mes.vendas, comissao: mes.comissao, acumulado: somar(corrente) }
  })
}

/** Divisão em centavos, sem float: `Number` de dinheiro é o defeito que o D3 fechou. */
function dividir(valor: string, por: number): string {
  const centavos = BigInt(Math.round(Number(valor) * 100))
  return (Number(centavos / BigInt(por)) / 100).toFixed(2)
}

function Composicao({
  clientes,
  produtos,
}: {
  clientes: readonly { nome: string; valor: string }[]
  produtos: readonly { nome: string; valor: string }[]
}) {
  if (clientes.length === 0 && produtos.length === 0) return null
  return (
    <section className="painel" data-teste="composicao">
      <div className="titulo">
        <h2>De onde vem a sua comissão</h2>
      </div>
      {/*
        Duas colunas com a altura do que cada uma mostra (items-start), e o título do cartão em
        eyebrow, como o cartão do app interno (dashboard.tsx:232; D-134 · DS-531).
      */}
      <div className="grade-composicao">
        <article className="cartao-item">
          <p className="eyebrow">Por cliente</p>
          <Barras fatias={clientes} />
        </article>
        <article className="cartao-item">
          <p className="eyebrow">Por produto</p>
          <Barras fatias={produtos} />
        </article>
      </div>
    </section>
  )
}

/**
 * A barra de participação, na régua do app interno (dashboard.tsx:234-241): o nome à esquerda, que
 * corta com o texto no title, e o valor curto com o percentual à direita, sem quebrar (P09, P49).
 * Os valores estão escritos em cada linha: por isso a barra não ganha balão (fluxo-de-caixa.tsx:941-947).
 */
function Barras({ fatias }: { fatias: readonly { nome: string; valor: string }[] }) {
  const total = fatias.reduce((soma, f) => soma + Number(f.valor), 0)
  return (
    <ul className="barras">
      {fatias.map((fatia) => {
        const parte = total === 0 ? 0 : Math.round((Number(fatia.valor) / total) * 100)
        return (
          <li key={fatia.nome}>
            <span className="barra-rotulo">
              <span className="barra-nome" title={fatia.nome}>
                {fatia.nome}
              </span>
              <span className="barra-valor tnum" data-teste="valor-da-fatia">
                {brCurto(fatia.valor)} · {String(parte)}%
              </span>
            </span>
            <span className="barra" aria-hidden="true">
              <span style={{ width: `${String(parte)}%` }} />
            </span>
          </li>
        )
      })}
    </ul>
  )
}

function Kpi({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <article>
      <small>{rotulo}</small>
      <strong>{valor}</strong>
    </article>
  )
}

export type { LinhaDoExtratoDoParceiroNoPortal }
